import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { BrandMark, RingsIcon } from '../components/BrandMark';
import { GoogleButton } from '../components/GoogleButton';

export default function LoginPage() {
  const { login, loginWithGoogle, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) return;
    setBusy(true);
    loginWithToken(token)
      .then(() => navigate('/', { replace: true }))
      .catch(() => toast.error('הכניסה עם Google נכשלה'))
      .finally(() => setBusy(false));
  }, [loginWithToken, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate('/');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'אימייל או סיסמה שגויים');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full grid lg:grid-cols-2" dir="ltr">
      {/* טופס — שמאל כמו במוקאפ */}
      <section
        dir="rtl"
        className="flex items-center justify-center px-5 py-12 sm:px-10 bg-paper"
      >
        <div className="w-full max-w-[420px] animate-scale-in">
          <div className="surface !shadow-deep px-7 py-9 sm:px-9 sm:py-10">
            <div className="flex justify-center mb-7">
              <BrandMark size="md" showTagline={false} />
            </div>

            <div className="text-center mb-8">
              <h1 className="font-sans text-[1.65rem] sm:text-3xl font-bold text-ink tracking-tight">
                ברוכים הבאים
              </h1>
              <p className="text-ink-mute mt-2 text-[15px]">התחברו לחשבון הזוג שלכם</p>
            </div>

            <form onSubmit={submit} className="space-y-4" noValidate>
              <GoogleButton onClick={loginWithGoogle} disabled={busy} />
              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-line" />
                <span className="text-[12px] text-ink-faint font-medium">או עם אימייל</span>
                <div className="h-px flex-1 bg-line" />
              </div>

              <div>
                <label htmlFor="email" className="label">
                  אימייל
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="field"
                  dir="ltr"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="password" className="label">
                  סיסמה
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="field !pe-12"
                    required
                    aria-required="true"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink transition-colors cursor-pointer p-2 min-h-touch min-w-[44px] grid place-items-center rounded-full"
                    aria-label={showPass ? 'הסתר סיסמה' : 'הצג סיסמה'}
                    aria-pressed={showPass}
                  >
                    {showPass ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                  </button>
                </div>
              </div>

              <p className="text-[12px] text-ink-faint text-center -mt-1">
                נרשמתם מהאפליקציה עם Google? השתמשו בכפתור למעלה — אין סיסמה לחשבון כזה.
              </p>

              <button type="submit" disabled={busy} className="btn-primary w-full mt-3 !py-3.5 text-[16px]">
                {busy ? (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin"
                      aria-hidden
                    />
                    מתחבר…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    כניסה
                    <ArrowLeft size={17} aria-hidden />
                  </span>
                )}
              </button>
            </form>

            <p className="mt-7 text-center text-[14px] text-ink-mute">
              עדיין אין חשבון?{' '}
              <Link
                to="/register"
                className="text-sage-700 font-semibold hover:underline underline-offset-2"
              >
                הרשמת זוג
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* פאנל מותג — ימין */}
      <aside className="brand-panel hidden lg:flex flex-col justify-between p-12" aria-hidden="true">
        <RingsIcon className="brand-panel-rings w-[280px] h-[180px] -top-8 -left-10" strokeWidth={1.5} />
        <RingsIcon
          className="brand-panel-rings w-[320px] h-[200px] -bottom-12 -right-16 rotate-12"
          strokeWidth={1.5}
        />

        {/* טבעות זהב תלת-ממדיות צפות */}
        <span className="ring-float w-24 h-24 top-[18%] right-[14%]" />
        <span
          className="ring-float w-14 h-14 top-[34%] right-[30%]"
          style={{ animationDelay: '1.8s', animationDuration: '9s' }}
        />
        <span
          className="ring-float w-9 h-9 bottom-[28%] left-[16%]"
          style={{ animationDelay: '0.9s', animationDuration: '8s' }}
        />

        <div className="relative z-10">
          <BrandMark variant="light" size="md" showTagline={false} />
        </div>

        <div className="relative z-10 max-w-md space-y-5">
          <h2 className="font-sans text-4xl xl:text-5xl font-bold leading-[1.2] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(90,48,61,0.4)]">
            ניהול החתונה
            <br />
            בשקט ובנוחות.
          </h2>
          <p className="text-white/85 text-lg leading-relaxed font-light">
            אישורי הגעה, טרמפים וקישורים אישיים — בממשק רך וברור לזוג ולמוזמנים.
          </p>

          <div className="flex flex-wrap gap-2 pt-1">
            {['אישורי הגעה', 'טרמפים', 'מתנות', 'WhatsApp'].map((t) => (
              <span
                key={t}
                className="rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-white border border-white/25 bg-white/10 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(90,48,61,0.4)]"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-[11px] tracking-[0.2em] uppercase text-white/70 font-medium">
          Wedding Operating System
        </p>
      </aside>
    </div>
  );
}
